import { ISelectFieldOption } from '@/components/fields/SelectField';
import {
	BubbleDataPoint,
	Chart,
	ChartTypeRegistry,
	Plugin,
	Point,
	ScriptableScaleContext,
} from 'chart.js';
import { AnyObject } from 'chart.js/dist/types/basic';

export const buildParams = (params: {
	[key: string]: TValue | TValue[] | undefined;
}) => {
	const query = Object.entries(params)
		.map(
			([key, value]) =>
				`${encodeURIComponent(key)}=${encodeURIComponent(value as string)}`
		)
		.join('&');

	return query ? `?${query}` : '';
};

export const convertDemandToHierarchy = (data: TDemandTree) => {
	const hierarchy: Record<string, TreeNode> = {};

	for (const item of data) {
		const { global_region, sales_region, family_name, family_code } = item;

		if (!hierarchy[global_region]) {
			hierarchy[global_region] = {
				id: global_region,
				name: global_region,
				value: global_region,
				additionalData: {
					type: 'global_region',
				},
				children: [],
			};
		}

		const salesRegionNode = hierarchy[global_region].children!.find(
			(node) => node.name === sales_region
		);

		if (!salesRegionNode) {
			const newSalesRegionNode = {
				id: sales_region,
				name: sales_region,
				value: sales_region,
				additionalData: {
					type: 'sales_region',
					item,
				},
				children: [],
			};
			hierarchy[global_region].children!.push(newSalesRegionNode);
		}

		const familyNode = {
			id: `${sales_region}-${family_code}`,
			name: family_name,
			value: family_code,
			additionalData: {
				type: 'family_code',
				item,
			},
			children: [],
		};
		const salesRegion = hierarchy[global_region].children!.find(
			(node) => node.name === sales_region
		);
		if (salesRegion) {
			salesRegion.children!.push(familyNode);
		}
	}

	return Object.values(hierarchy);
};

export const convertCapacityToHierarchy = (data: TCapacityTree) => {
	const hierarchy: Record<string, TreeNode> = {};

	for (const item of data) {
		const { global_region, production_region, plant, plant_code } = item;

		if (!hierarchy[global_region]) {
			hierarchy[global_region] = {
				id: global_region,
				name: global_region,
				value: global_region,
				additionalData: {
					type: 'global_region',
				},
				children: [],
			};
		}

		const productionRegionNode = hierarchy[global_region].children!.find(
			(node) => node.name === production_region
		);

		if (!productionRegionNode) {
			const newProductionRegion = {
				id: production_region,
				name: production_region,
				value: production_region,
				additionalData: {
					type: 'production_region',
				},
				children: [],
			};
			hierarchy[global_region].children!.push(newProductionRegion);
		}

		const familyNode = {
			id: `${production_region}-${plant}-${plant_code}`,
			name: `${plant} (${plant_code})`,
			value: plant_code,
			additionalData: {
				type: 'plant_code',
				item,
			},
			children: [],
		};
		const productionRegion = hierarchy[global_region].children!.find(
			(node) => node.name === production_region
		);
		if (productionRegion) {
			productionRegion.children!.push(familyNode);
		}
	}

	return Object.values(hierarchy);
};

export const formatNum = ({
	value,
	scale = 0,
	formatThousands = true,
	round = true,
}: {
	value: number | null | undefined;
	scale?: number;
	formatThousands?: boolean;
	round?: boolean;
}) => {
	if (!(value || value === 0)) return '';
	if (typeof value !== 'number') return 'error number';

	value = round ? Math.round(value) : value;
	const formattedValue = scale ? value.toFixed(scale) : value.toString();
	return formatThousands
		? formattedValue.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
		: formattedValue;
};

export const formatPct = (
	value: number | null | undefined,
	roundPct?: boolean,
	scale?: number
) => {
	if (value === null || value === undefined) return '';
	if (typeof value !== 'number') return 'error number';
	if (roundPct) {
		const vo = Math.abs(value);
		scale = vo < 0.0001 ? 6 : vo < 0.001 ? 4 : vo < 1 ? 2 : 0;
	} else {
		scale = 6;
	}
	let s = formatNum({ value: value * 100, scale });
	if (s) {
		if ((scale === 6 || scale >= 4) && s.endsWith('00')) {
			s = s.slice(0, -2);
		}

		if (scale >= 2 && s.endsWith('00')) {
			s = s.slice(0, -3);
		}
		s += '%';
	}
	return s;
};

export const string2Number = (s: string): number | undefined => {
	s = s.replace(',', '.');
	s = s.replace(/[., %]/g, '');
	s = s.replace('@', '.');

	const f = parseFloat(s);
	return typeof f === 'number' && isFinite(f) ? f : undefined;
};

export const string2Pct = (s: string): number | undefined => {
	const o = string2Number(s);
	return o ? o / 100 : o;
};

export const exists = (v: any) => v !== null && v !== undefined;

export const joinClassNames = (...args: (TValue | undefined | false)[]) =>
	args.filter(exists).join(' ');

export const isInRange = (value: number, range: [number, number]) =>
	value >= range[0] && value <= range[1];

export const today = () => {
	const d = new Date();
	d.setUTCHours(0, 0, 0, 0);

	return d;
};

export const currentYear = () => new Date().getUTCFullYear();

export const addYears = (date: Date, years: number) =>
	new Date(
		Date.UTC(
			date.getUTCFullYear() + years,
			date.getUTCMonth(),
			date.getUTCDate()
		)
	);

export const formatDate = ({
	date,
	delimiter = '/',
	dateOnly = true,
}: {
	date: Date | null | undefined;
	delimiter?: '/' | '-' | '.' | '_';
	dateOnly?: boolean;
}) => {
	if (!exists(date)) return '';
	if (!(date instanceof Date)) return 'error date';
	const y = date.getUTCFullYear();
	const m = ('0' + (date.getUTCMonth() + 1)).slice(-2);
	const d = ('0' + date.getUTCDate()).slice(-2);
	const hh = date.getUTCHours();
	const mm = date.getUTCMinutes();
	const ss = date.getUTCSeconds();

	let result = `${d}${delimiter}${m}${delimiter}${y}`;
	if (!dateOnly && (hh || mm || ss)) {
		result = `${result} ${('0' + hh).slice(-2)}:${('0' + mm).slice(-2)}:${(
			'0' + ss
		).slice(-2)}`;
	}
	return result;
};

export const wait = (ms: number) =>
	new Promise((resolve) => setTimeout(resolve, ms));

export const convertScenariosToList = (scenarios: TScenario[]) =>
	scenarios.map((scenario) => ({
		name: scenario.name,
		label: scenario.name,
		value: scenario.id,
	}));

export const getScenarioCurrentRange = (
	scenario: Record<string | number, TValue>[]
): [number, number] | null => {
	const years = Object.keys(scenario[0] ?? []);
	if (years) {
		return [+years[0], +years.at(-1)!];
	}

	return null;
};

export const isTupleInRange = <T>(values: [T, T], range: [T, T]) => {
	return values[0] < range[0] || values[1] > range[1];
};

export const YEARS_TO_ADD = 10;
export const YEARS_TO_SUBTRACT = 5;
export const CURRENT_YEAR = currentYear();
export const BASE_YEAR = 2023;

export const defaultFontChartAtZero = (ctx: ScriptableScaleContext) => {
	const value = ctx?.tick?.value;
	if (value === 0) {
		return {
			weight: 'bold',
		};
	}
	return {
		color: 'gray',
	};
};

export const defaultColorChartAtZero = (ctx: ScriptableScaleContext) => {
	const value = ctx?.tick?.value;
	if (value === 0) {
		return 'black';
	}
	return 'gray';
};

export const drawLineAtZeroPlugin: Plugin<'bar', AnyObject> = {
	id: 'drawLineAtZeroPlugin',
	afterDatasetsDraw: (
		chart: Chart<
			keyof ChartTypeRegistry,
			(number | [number, number] | Point | BubbleDataPoint | null)[],
			unknown
		>
	) => {
		const {
			ctx,
			chartArea: { left, right },
			scales: { y1 },
		} = chart;
		ctx.save();
		ctx.beginPath();
		ctx.moveTo(right, y1.getPixelForValue(0));
		ctx.lineTo(left, y1.getPixelForValue(0));
		ctx.setLineDash([5, 5]);
		ctx.strokeStyle = 'rgba(0,0,0,0.5)';
		ctx.stroke();
	},
};

export const sortCapacityScenarios = (
	scenarios: TCapacityScenarioData[],
	capacityTypesOrder: Record<string, number>
) =>
	[...scenarios].sort((a, b) => {
		if (
			a.capacity_origin === 'Capacité réelle' &&
			b.capacity_origin !== 'Capacité réelle'
		) {
			return -1;
		} else if (
			a.capacity_origin !== 'Capacité réelle' &&
			b.capacity_origin === 'Capacité réelle'
		) {
			return 1;
		}
		const aOrder = capacityTypesOrder[a.capacity_type];
		const bOrder = capacityTypesOrder[b.capacity_type];

		return aOrder < bOrder ? -1 : aOrder > bOrder ? 1 : 0;
	});

export const getEpvFamilyById = (
	epvFamilies: TEpvFamily[] | undefined,
	id: number
) => {
	return epvFamilies?.find((f) => f.epv_family_lvl2_id === id);
};

export const groupEpvFamilies = (
	epvFamilies: TEpvFamily[]
): (ISelectFieldOption & { children: ISelectFieldOption[] })[] => {
	const groupChildren = (data: TEpvFamily) => ({
		label: data.epv_family_lvl2,
		value: data.epv_family_lvl2_id,
	});

	const result: (ISelectFieldOption & { children: ISelectFieldOption[] })[] =
		[];

	epvFamilies.forEach((item) => {
		const {
			epv_family_lvl1,
			epv_family_lvl1_id,
			epv_family_lvl2_id,
			epv_family_lvl2,
		} = item;

		const existingItem = result.find((item) => item.label === epv_family_lvl1);

		if (existingItem) {
			existingItem.children.push({
				label: epv_family_lvl2,
				value: epv_family_lvl2_id,
			});
		} else {
			result.push({
				label: epv_family_lvl1,
				value: epv_family_lvl1_id,
				children: [groupChildren(item)],
			});
		}
	});

	return result;
};
